import React, { useState, useEffect } from "react";

import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

const ContactListModal = ({ open, onClose, contactListId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
  };

  const [contactList, setContactList] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    const fetchContactList = async () => {
      if (!contactListId) return;
      try {
        const { data } = await api.get(`/contact-lists/${contactListId}`);
        setContactList((prevState) => {
          return { ...prevState, ...data };
        });
      } catch (err) {
        toastError(err);
      }
    };

    fetchContactList();
  }, [contactListId, open]);

  const handleClose = () => {
    onClose();
    setContactList(initialState);
    setNameError("");
    setSaving(false);
  };

  const handleNameChange = (event) => {
    setContactList(prevState => ({ ...prevState, name: event.target.value }));
    if (nameError) setNameError("");
  };

  const handleSaveContactList = async () => {
    const name = String(contactList.name || "").trim();
    if (name.length < 2) {
      setNameError("Informe um nome com pelo menos 2 caracteres.");
      return;
    }

    setSaving(true);
    try {
      if (contactListId) {
        await api.put(`/contact-lists/${contactListId}`, { name });
      } else {
        await api.post("/contact-lists", { name });
      }
      toast.success(i18n.t("contactList.dialog"));
      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {contactListId
            ? `${i18n.t("contactLists.dialog.edit")}`
            : `${i18n.t("contactLists.dialog.add")}`}
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.multFieldLine}>
            <TextField
              label={i18n.t("contactLists.dialog.name")}
              autoFocus
              value={contactList.name || ""}
              onChange={handleNameChange}
              error={Boolean(nameError)}
              helperText={nameError}
              variant="outlined"
              margin="dense"
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={saving}
            variant="outlined"
          >
            {i18n.t("contactLists.dialog.cancel")}
          </Button>
          <Button
            color="primary"
            disabled={saving}
            variant="contained"
            className={classes.btnWrapper}
            onClick={handleSaveContactList}
          >
            {contactListId
              ? `${i18n.t("contactLists.dialog.okEdit")}`
              : `${i18n.t("contactLists.dialog.okAdd")}`}
            {saving && (
              <CircularProgress
                size={24}
                className={classes.buttonProgress}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactListModal;
